import {
  BankingAccountDetailedFragment,
  BankingOnboardingFragment,
  BankingValueObjectsOnboardingStatusEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import {
  bankAccountLastDigits,
  bankingAccountActions,
  getDefaultAlias,
  mapStatusToF0Variant,
  mapStatusToText,
} from 'modules/banking/utils'

import { AccountListItem } from './types'

export const getAlertMessage = (
  requiredActions?: readonly string[] | null,
  status?: BankingValueObjectsOnboardingStatusEnum | null
): string | undefined => {
  // First check requiredActions
  if (requiredActions?.length) {
    if (requiredActions.includes('check_email')) {
      return i18n.t('banking.onboarding.check_email_modal.title')
    }
    if (requiredActions.includes('complete_identification')) {
      return i18n.t('banking.accounts.alert.complete_identification')
    }
    if (requiredActions.includes('upload_document')) {
      return i18n.t('banking.accounts.alert.upload_document')
    }
    return i18n.t('banking.accounts.alert.action_required')
  }

  // Then check status for alert message - these statuses require user action
  if (
    status === 'missing_information' ||
    status === 'pending' ||
    status === 'verification_pending'
  ) {
    return i18n.t('banking.accounts.alert.action_required')
  }

  return undefined
}

export const getActionInfo = (
  requiredActions?: readonly string[] | null,
  status?: BankingValueObjectsOnboardingStatusEnum | null,
  hasOnboardingUrl?: boolean
): { actionText?: string; isExternalAction: boolean } => {
  // First check requiredActions
  if (requiredActions?.length) {
    // check_email doesn't require external action - just informational
    if (requiredActions.includes('check_email')) {
      return {
        actionText: i18n.t('banking.accounts.action.check_email'),
        isExternalAction: false,
      }
    }

    // These require external navigation
    if (requiredActions.includes('complete_identification')) {
      return {
        actionText: i18n.t('banking.accounts.action.complete_identification'),
        isExternalAction: true,
      }
    }

    if (requiredActions.includes('upload_document')) {
      return {
        actionText: i18n.t('banking.accounts.action.upload_document'),
        isExternalAction: true,
      }
    }

    // Default for other required actions
    return {
      actionText: i18n.t('banking.accounts.action.continue'),
      isExternalAction: true,
    }
  }

  // If no requiredActions, check status - these statuses require user action
  const needsAction =
    status === 'missing_information' ||
    status === 'pending' ||
    status === 'verification_pending'

  if (needsAction) {
    return {
      actionText: i18n.t('banking.accounts.action.continue'),
      isExternalAction: hasOnboardingUrl ?? false,
    }
  }

  return { isExternalAction: false }
}

export const mapAccountsToListItems = (
  accounts: BankingAccountDetailedFragment[],
  onboardings: BankingOnboardingFragment[],
  locale: string
): AccountListItem[] => {
  const items: AccountListItem[] = []

  // 1. Onboardings (not completed) -> "in_review" group
  onboardings
    .filter((o) => o.status !== 'completed')
    .forEach((onboarding) => {
      const hasRequiredActions = (onboarding.requiredActions?.length ?? 0) > 0
      const statusNeedsAction =
        onboarding.status === 'missing_information' ||
        onboarding.status === 'pending' ||
        onboarding.status === 'verification_pending'
      const needsAction = hasRequiredActions || statusNeedsAction
      const actionInfo = getActionInfo(
        onboarding.requiredActions,
        onboarding.status,
        Boolean(onboarding.onboardingUrl)
      )
      items.push({
        id: `onboarding_${onboarding.id}`,
        title: getDefaultAlias(onboarding.creationSource),
        description: onboarding.legalEntity?.legalName || '',
        groupStatus: 'in_review',
        onboarding,
        legalEntityId: onboarding.legalEntity?.id,
        logoUrl: onboarding.institution?.logo,
        institutionName: onboarding.institution?.name,
        statusText: mapStatusToText(onboarding.status),
        statusVariant: mapStatusToF0Variant(onboarding.status),
        hasAlert: needsAction,
        alertMessage: getAlertMessage(onboarding.requiredActions, onboarding.status),
        alertLevel: 'warning',
        actionText: actionInfo.actionText,
        isExternalAction: actionInfo.isExternalAction,
      })
    })

  // 2. Completed accounts -> "active" group
  accounts
    .filter((a) => a.onboarding?.status === 'completed')
    .forEach((account) => {
      account.bankAccounts.forEach((bankAccount) => {
        const { hasToReconnect } = bankingAccountActions(account, bankAccount)
        items.push({
          id: `account_${account.id}_bank_${bankAccount.id}`,
          title: bankAccount.accountAlias || account.institution?.name || '',
          description: account.legalEntity?.legalName || '',
          groupStatus: 'active',
          account,
          bankAccount,
          legalEntityId: account.legalEntity?.id,
          logoUrl: account.institution?.logo,
          institutionName: account.institution?.name,
          accountLastDigits: bankAccountLastDigits(bankAccount),
          balanceText: formattedAmount({
            amountCents: bankAccount.accountBalanceCents,
            currency: bankAccount.currency,
            locale,
          }),
          hasAlert: hasToReconnect,
          alertMessage: hasToReconnect
            ? i18n.t('banking.accounts.status.expired')
            : undefined,
          alertLevel: 'critical',
        })
      })
    })

  return items
}
